import { File, FileArchive, FileCode, FileImage, FileSpreadsheet, FileText, FileType, Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { onLock, useIsUnlocked } from "@/features/crypto/keyring";
import { decryptFileMeta, scopeOfFile, type FileMeta } from "@/features/crypto/ops";
import type { FileRow } from "./types";

type FileLike = Pick<FileRow, "filename" | "mime" | "encrypted"> & { encMeta?: string | null };

export function fileTypeLabel(f: Pick<FileRow, "filename" | "mime">) {
  const m = f.mime;
  if (m === "application/pdf") return "PDF";
  if (m.startsWith("image/")) return "Image";
  if (/csv|spreadsheet|excel|tab-separated/.test(m) || /\.(csv|tsv|xlsx?|ods)$/i.test(f.filename)) return "Spreadsheet";
  if (/zip|tar|gzip|compressed|7z/.test(m) || /\.(zip|tar|gz|tgz|7z)$/i.test(f.filename)) return "Archive";
  if (/\.(py|r|sh|js|ts|ipynb|m|jl|do|sas|sql)$/i.test(f.filename)) return "Code";
  if (/word|opendocument\.text|rtf/.test(m) || /\.(docx?|odt|rtf)$/i.test(f.filename)) return "Document";
  if (m.startsWith("text/") || /json|yaml|xml/.test(m) || /\.(md|txt|json|ya?ml|tex|bib|log)$/i.test(f.filename)) return "Text";
  return "Other";
}

export function FileIcon({ file, className }: { file: FileLike; className?: string }) {
  const cls = cn("size-4 shrink-0 text-muted-foreground", className);
  if (file.encrypted) return <Lock className={cls} aria-hidden />;
  switch (fileTypeLabel(file)) {
    case "PDF":
      return <FileType className={cls} aria-hidden />;
    case "Image":
      return <FileImage className={cls} aria-hidden />;
    case "Spreadsheet":
      return <FileSpreadsheet className={cls} aria-hidden />;
    case "Archive":
      return <FileArchive className={cls} aria-hidden />;
    case "Code":
      return <FileCode className={cls} aria-hidden />;
    case "Document":
    case "Text":
      return <FileText className={cls} aria-hidden />;
    default:
      return <File className={cls} aria-hidden />;
  }
}

/** File name with a type icon. End-to-end encrypted files show a lock until decrypted in the browser. */
export function FileName({ file, className }: { file: FileLike; className?: string }) {
  const meta = useFileMeta(file);
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1.5", className)} title={file.encrypted ? "End-to-end encrypted file" : undefined}>
      <FileIcon file={file} />
      <span className="truncate">{file.encrypted ? (meta?.filename ?? "Encrypted file") : file.filename}</span>
    </span>
  );
}

const metaCache = new Map<string, FileMeta>();

/** Real name/type/size of an encrypted file, decrypted in the browser once its key is unlocked. */
export function useFileMeta(file: { encrypted: boolean; encMeta?: string | null }): FileMeta | null {
  const scopeId = file.encrypted ? scopeOfFile(file.encMeta) : null;
  const unlocked = useIsUnlocked(scopeId);
  const [meta, setMeta] = useState<FileMeta | null>(() => (file.encMeta && unlocked ? (metaCache.get(file.encMeta) ?? null) : null));
  useEffect(() => {
    if (!file.encrypted || !file.encMeta || !unlocked) return setMeta(null);
    const hit = metaCache.get(file.encMeta);
    if (hit) return setMeta(hit);
    let alive = true;
    decryptFileMeta(file.encMeta)
      .then((m) => {
        metaCache.set(file.encMeta!, m);
        if (alive) setMeta(m);
      })
      .catch(() => alive && setMeta(null));
    return () => {
      alive = false;
    };
  }, [file.encrypted, file.encMeta, unlocked]);
  return meta;
}

onLock(() => metaCache.clear());
